/*
  Custom client-side collection filters for Club Partner.
  Reads a JSON product index embedded (or fetched in the background) on the
  collection page, then filters/renders entirely in the browser with no page
  reloads. See assets/custom-filter-config.js for the editable filter config
  (labels, typo fixes, groupings, and renames for every filter).
*/
(function () {
  'use strict';

  var FILTER_FIELDS = [
    { key: 'club_type' },
    { key: 'shaft_type' },
    { key: 'club_number' },
    { key: 'flex' },
    { key: 'handedness' },
    { key: 'brand' },
    { key: 'shaft_tip_brand' },
    { key: 'condition' },
    { key: 'loft' },
    { key: 'shaft_material' },
    { key: 'lie_angle_color' }
  ];

  // The "Sort by" dropdown shown above the product grid. Edit the labels
  // here to change what shoppers see. The FIRST entry is the default when
  // the page loads. "featured" means the collection's own order, i.e.
  // whatever order Shopify hands the products over in.
  var SORT_OPTIONS = [
    { key: 'featured',   label: 'Featured' },
    { key: 'price-asc',  label: 'Price: Low to High' },
    { key: 'price-desc', label: 'Price: High to Low' },
    { key: 'date-desc',  label: 'Date Listed: Newest First' },
    { key: 'date-asc',   label: 'Date Listed: Oldest First' }
  ];

  var CACHE_TTL_MS = 6 * 60 * 60 * 1000; // 6 hours

  // URL of the theme's assets folder, read off the page (see the section
  // file's data-asset-base). Brand logo file names are appended to it.
  // Module-level because renderCard is a static helper with no instance.
  var ASSET_BASE = '';

  // Lazily-built lookup of brand name -> logo file name, from the "logos"
  // block in custom-filter-config.js. Keys are lowercased so a brand
  // written slightly differently in the config still matches.
  var brandLogoMap = null;

  function brandLogoFile(brand) {
    if (brandLogoMap === null) {
      brandLogoMap = {};
      var logos = (window.CustomFilterFieldConfig || {});
      logos = (logos.brand || {}).logos || {};
      Object.keys(logos).forEach(function (name) {
        if (logos[name]) brandLogoMap[String(name).trim().toLowerCase()] = String(logos[name]).trim();
      });
    }
    if (!brand) return '';
    return brandLogoMap[String(brand).trim().toLowerCase()] || '';
  }

  // Fallback name of the section that renders nothing but the product index
  // JSON. Background loads of the index point at that section instead of the
  // full collection section, so each trip fetches ~40KB of data rather than
  // ~800KB of page markup.
  //
  // Normally the real id is read off the embedded chunk on the page, because
  // Shopify's id for a section inside a JSON template is the long form
  // (template--123__custom_filter_chunk) and only that form works with
  // ?section_id=. This short key is just a last resort if that read fails.
  var INDEX_SECTION_ID = 'custom_filter_chunk';
  var DEFAULT_PAGE_SIZE = 36;

  // Where the search box gets "every product in the store" from. This is a
  // collection Shopify maintains for you containing every published product.
  var SEARCH_ALL_URL = '/collections/all';

  // Wording on the bubble shown over the photo of a club that has a
  // crossed-out original price. Matches the theme's own "on sale" label, so
  // the search page, product pages and these cards all say the same thing.
  var SALE_BADGE_TEXT = 'CLEARANCE';
  // The theme's sale-badge colour (Theme settings > Badges > Sale badge).
  var SALE_BADGE_SCHEME = 'scheme-4';

  // How long a shopper's typing has to settle before we re-filter. Short
  // enough to feel instant, long enough not to re-render on every letter.
  var SEARCH_DEBOUNCE_MS = 140;

  var ICON_FILTER =
    '<svg class="custom-filters__icon" viewBox="0 0 20 20" aria-hidden="true" focusable="false">' +
    '<path d="M2.5 5h15M5.5 10h9M8.5 15h3" fill="none" stroke="currentColor" ' +
    'stroke-width="1.6" stroke-linecap="round"/></svg>';

  var ICON_SEARCH =
    '<svg class="custom-filters__icon" viewBox="0 0 20 20" aria-hidden="true" focusable="false">' +
    '<circle cx="8.75" cy="8.75" r="5.25" fill="none" stroke="currentColor" stroke-width="1.6"/>' +
    '<path d="M12.6 12.6 17 17" fill="none" stroke="currentColor" stroke-width="1.6" ' +
    'stroke-linecap="round"/></svg>';

  // Builds the text the search box looks through, once per product. Titles
  // here are very descriptive ("Ping Blueprint S Iron Set 5-PW Orange Dot NS
  // Pro Tour105 Stiff Flex +1/4\" MINT"), so title + vendor + type is plenty
  // to find anything by brand, model, size, flex or condition.
  function buildSearchText(p) {
    return [p.title, p.vendor, p.type].filter(Boolean).join(' ').toLowerCase();
  }

  // Attaches the per-product data the filters and search need. Safe to call
  // more than once on the same array — it skips products already prepared.
  function prepareProducts(list, instance) {
    list.forEach(function (p) {
      if (!p._search) p._search = buildSearchText(p);
      if (!p._values) instance.computeValues(p);
    });
    return list;
  }

  // Every word the shopper typed must appear somewhere in the product's text,
  // in any order. "ping blueprint" and "blueprint ping" both work.
  function searchTokens(query) {
    return String(query).toLowerCase().split(/\s+/).filter(Boolean);
  }

  function matchesSearch(product, tokens) {
    for (var i = 0; i < tokens.length; i++) {
      if (product._search.indexOf(tokens[i]) === -1) return false;
    }
    return true;
  }

  function debounce(fn, wait) {
    var t;
    return function () {
      var args = arguments, ctx = this;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(ctx, args); }, wait);
    };
  }

  function escapeHtml(value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function slugify(value) {
    return String(value).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
  }

  // Reduces text to lowercase letters and numbers only, so the filter search
  // boxes ignore spaces, dots and dashes: typing "labgolf" finds "L.A.B.
  // Golf" and "topflite" finds "Top-Flite".
  function squash(value) {
    return String(value == null ? '' : value).toLowerCase().replace(/[^a-z0-9]+/g, '');
  }

  // Splits what the shopper typed into words, each squashed the same way.
  function queryTokens(raw) {
    return String(raw == null ? '' : raw).trim().split(/\s+/).map(squash).filter(Boolean);
  }

  // Tidies up a raw Shopify value's capitalisation for display.
  // A value that already has capitals of its own is left EXACTLY as the shop
  // typed it, so "MacGregor", "EZRoll", "iDrive", "KZG" and "PGA" all come
  // through intact — no rule could guess those. Only an all-lowercase value
  // gets capitalised, so "ben hogan" reads as "Ben Hogan".
  // Two spellings that differ only in capitals ("MacGregor" / "Macgregor")
  // would show as two options, so those get an entry in that filter's
  // "fixes" block in custom-filter-config.js, which merges them.
  function prettyCase(value) {
    var raw = String(value == null ? '' : value).trim();
    if (!raw || /[A-Z]/.test(raw)) return raw;
    return raw.replace(/(^|[^a-z0-9'])([a-z])/g, function (m, before, letter) {
      return before + letter.toUpperCase();
    });
  }

  function splitMultiValue(raw) {
    if (!raw) return [];
    return String(raw).split(',').map(function (s) { return s.trim(); }).filter(Boolean);
  }

  function naturalCompare(a, b) {
    var an = parseFloat(a), bn = parseFloat(b);
    var aIsNum = !isNaN(an) && /^-?\d+(\.\d+)?$/.test(String(a).trim());
    var bIsNum = !isNaN(bn) && /^-?\d+(\.\d+)?$/.test(String(b).trim());
    if (aIsNum && bIsNum) return an - bn;
    if (aIsNum) return -1;
    if (bIsNum) return 1;
    return String(a).localeCompare(String(b));
  }

  // Pulls a leading number out of a string ("9.5°" -> 9.5) for numeric sort.
  // Returns null if the string doesn't start with a number.
  function parseLeadingNumber(value) {
    var m = String(value).match(/-?\d+(\.\d+)?/);
    return m ? parseFloat(m[0]) : null;
  }

  function numericCompare(a, b) {
    var an = parseLeadingNumber(a), bn = parseLeadingNumber(b);
    if (an !== null && bn !== null) return an - bn;
    if (an !== null) return -1;
    if (bn !== null) return 1;
    return String(a).localeCompare(String(b));
  }

  function titleCaseKey(key) {
    return String(key).split('_').map(function (word) {
      return word.charAt(0).toUpperCase() + word.slice(1);
    }).join(' ');
  }

  function fieldConfig(fieldKey) {
    var all = window.CustomFilterFieldConfig || {};
    return all[fieldKey] || {};
  }

  function fieldLabel(fieldKey) {
    return fieldConfig(fieldKey).label || titleCaseKey(fieldKey);
  }

  // Builds a normalize(rawValue) function for one filter field, driven by
  // that field's entry (if any) in window.CustomFilterFieldConfig. Used for
  // every filter, including brand — brand's config entry reproduces its
  // previous dedicated behavior exactly (fixes + groups + catch-all Other).
  function buildFieldNormalizer(fieldKey) {
    var cfg = fieldConfig(fieldKey);
    var fixes = cfg.fixes || {};
    var groups = cfg.groups || {};
    var catchAllUnlisted = !!cfg.catchAllUnlisted;
    var otherLabel = cfg.otherLabel || 'Other';

    var valueToGroup = {};
    Object.keys(groups).forEach(function (groupLabel) {
      (groups[groupLabel] || []).forEach(function (rawValue) {
        valueToGroup[String(rawValue).trim().toLowerCase()] = groupLabel;
      });
    });

    return function normalize(rawValue) {
      if (!rawValue) return catchAllUnlisted ? otherLabel : rawValue;
      var v = String(rawValue).trim();
      var vLower = v.toLowerCase();
      if (fixes[vLower]) {
        v = String(fixes[vLower]).trim();
        vLower = v.toLowerCase();
      }
      if (valueToGroup[vLower]) return valueToGroup[vLower];
      return catchAllUnlisted ? otherLabel : v;
    };
  }

  // ---- Filter search boxes ----------------------------------------------
  // Only filters with "search: true" in custom-filter-config.js get a search
  // box of their own inside the sidebar. Brand is the one that has it.

  // Per-field lookup: a displayed option -> every spelling that should find
  // it. Built from the "groups" and "fixes" blocks, so typing "vokey",
  // "acushnet" or "titliest" all find Titleist, and "king cobra" finds Cobra.
  var optionAliasCache = {};

  function optionAliasMap(fieldKey) {
    if (optionAliasCache[fieldKey]) return optionAliasCache[fieldKey];
    var cfg = fieldConfig(fieldKey);
    var groups = cfg.groups || {};
    var fixes = cfg.fixes || {};
    var map = {};

    function add(label, alias) {
      var key = String(label).trim().toLowerCase();
      if (!map[key]) map[key] = [];
      var text = squash(alias);
      if (text && map[key].indexOf(text) === -1) map[key].push(text);
    }

    Object.keys(groups).forEach(function (label) {
      add(label, label);
      (groups[label] || []).forEach(function (raw) { add(label, raw); });
    });

    // A "fixes" entry points a misspelling at a corrected value. Whatever
    // option that corrected value ends up displaying as should be findable by
    // the misspelling too.
    Object.keys(fixes).forEach(function (misspelling) {
      var fixed = String(fixes[misspelling]).trim().toLowerCase();
      if (map[fixed]) { add(fixed, misspelling); return; }
      Object.keys(groups).forEach(function (label) {
        var members = (groups[label] || []).map(function (raw) {
          return String(raw).trim().toLowerCase();
        });
        if (members.indexOf(fixed) !== -1) add(label, misspelling);
      });
    });

    optionAliasCache[fieldKey] = map;
    return map;
  }

  // Does this option match what the shopper typed? Every word they typed has
  // to appear somewhere in the option's own name or one of its aliases.
  function optionMatchesQuery(fieldKey, value, tokens) {
    if (!tokens.length) return true;
    var map = optionAliasMap(fieldKey);
    var aliases = map[String(value).trim().toLowerCase()];
    var haystack = (aliases ? aliases.join(' ') + ' ' : '') + squash(value);
    for (var i = 0; i < tokens.length; i++) {
      if (haystack.indexOf(tokens[i]) === -1) return false;
    }
    return true;
  }

  // Is this one of the values deliberately listed in the config — a named
  // group, an entry in "order", or the "Other" bucket itself? Anything else
  // is an extra: a raw Shopify value that falls into "Other" and is shown
  // only when a shopper searches for it by name.
  var listedValueCache = {};

  function listedValueSet(fieldKey) {
    if (listedValueCache[fieldKey]) return listedValueCache[fieldKey];
    var cfg = fieldConfig(fieldKey);
    var set = {};
    Object.keys(cfg.groups || {}).forEach(function (label) {
      set[String(label).trim().toLowerCase()] = true;
    });
    (cfg.order || []).forEach(function (label) {
      set[String(label).trim().toLowerCase()] = true;
    });
    if (cfg.otherLabel) set[String(cfg.otherLabel).trim().toLowerCase()] = true;
    if (cfg.catchAllUnlisted) set['other'] = true;
    listedValueCache[fieldKey] = set;
    return set;
  }

  function isListedValue(fieldKey, value) {
    return listedValueSet(fieldKey)[String(value).trim().toLowerCase()] === true;
  }

  function isOtherLabel(fieldKey, value) {
    var cfg = fieldConfig(fieldKey);
    var label = cfg.otherLabel || (cfg.catchAllUnlisted ? 'Other' : null);
    if (!label) return false;
    return String(value).trim().toLowerCase() === String(label).trim().toLowerCase();
  }

  // True when a filter should hide its unlisted values until they're searched
  // for. Needs both flags, so no other filter's list is ever trimmed.
  function hidesUnlisted(fieldKey) {
    var cfg = fieldConfig(fieldKey);
    return !!(cfg.search && cfg.searchUnlisted);
  }

  // For a raw Shopify value that got bucketed into "Other", the tidied-up
  // name it should be searchable and tickable under in its own right —
  // "Ben Hogan", "Top-Flite" and so on. Returns '' when the filter has no
  // search box, when the value was already a listed option, or when using it
  // would clash with one of the named options.
  function unlistedDisplay(fieldKey, rawValue, groupedValue) {
    if (!hidesUnlisted(fieldKey)) return '';
    if (!rawValue) return '';
    if (!isOtherLabel(fieldKey, groupedValue)) return '';
    var raw = String(rawValue).trim();
    if (!raw) return '';
    var fixes = fieldConfig(fieldKey).fixes || {};
    var fixed = fixes[raw.toLowerCase()];
    var display = fixed ? String(fixed).trim() : prettyCase(raw);
    if (!display || isListedValue(fieldKey, display)) return '';
    return display;
  }

  // Sorts a field's distinct display values for the sidebar list, driven by
  // that field's config:
  //  - "order": an explicit list of labels to show first, in that order.
  //    Anything not in the list is sorted alphabetically (or numerically,
  //    see "sort" below) and appended after it.
  //  - "sort": set to "numeric" to sort values without an "order" position
  //    by their leading number (e.g. Loft) instead of alphabetically.
  //  - "otherLabel": if a field sets this (Brand's catch-all, or a manual
  //    "Other" group like Club Number's), that value is always pinned to
  //    the very end of the list, regardless of "order" or "sort" — this
  //    happens whether or not "catchAllUnlisted" is turned on.
  // Fields with none of the above keep sorting exactly as before.
  function sortFieldOptions(fieldKey, values) {
    var cfg = fieldConfig(fieldKey);
    // Matches the default the normalizer falls back to when catchAllUnlisted
    // is on but no explicit otherLabel was given, so pinning always lines up
    // with whatever value the normalizer actually produced.
    var otherLabel = cfg.otherLabel || (cfg.catchAllUnlisted ? 'Other' : null);
    var pinned = [];
    var rest = [];

    values.forEach(function (v) {
      if (otherLabel && v === otherLabel) pinned.push(v);
      else rest.push(v);
    });

    var fallbackCompare = cfg.sort === 'numeric' ? numericCompare : naturalCompare;

    if (cfg.order && cfg.order.length) {
      var rank = {};
      cfg.order.forEach(function (label, idx) {
        rank[String(label).trim().toLowerCase()] = idx;
      });
      rest.sort(function (a, b) {
        var aKey = String(a).trim().toLowerCase();
        var bKey = String(b).trim().toLowerCase();
        var ra = rank.hasOwnProperty(aKey) ? rank[aKey] : null;
        var rb = rank.hasOwnProperty(bKey) ? rank[bKey] : null;
        if (ra !== null && rb !== null) return ra - rb;
        if (ra !== null) return -1;
        if (rb !== null) return 1;
        return fallbackCompare(a, b);
      });
    } else {
      rest.sort(fallbackCompare);
    }

    return rest.concat(pinned);
  }

  function CustomFilters(root) {
    this.root = root;
    this.sectionId = root.getAttribute('data-section-id');
    this.collectionUrl = root.getAttribute('data-collection-url');
    this.pageSize = parseInt(root.getAttribute('data-products-per-page'), 10) || DEFAULT_PAGE_SIZE;
    this.columnsDesktop = parseInt(root.getAttribute('data-columns-desktop'), 10) || 4;
    this.columnsMobile = root.getAttribute('data-columns-mobile') || '2';
    if (!ASSET_BASE) ASSET_BASE = root.getAttribute('data-asset-base') || '';
    this.normalizers = {};
    FILTER_FIELDS.forEach(function (f) { this.normalizers[f.key] = buildFieldNormalizer(f.key); }, this);

    this.products = [];            // the pool currently being filtered
    this.collectionProducts = [];  // this collection's own products
    this.allProducts = null;       // every active product in the store (lazy)
    this.allState = 'idle';        // idle | loading | ready | failed
    this.searchQuery = '';
    this.masterOptions = {};
    this.state = {};
    FILTER_FIELDS.forEach(function (f) { this.state[f.key] = new Set(); }, this);
    this.state.price = { min: null, max: null };
    this.state.sort = SORT_OPTIONS[0].key;
    this.visibleCount = this.pageSize;
    this.openGroups = {};
    this.optionQuery = {};        // what's typed in each filter's own search box

    this.sidebarEl = null;
    this.gridWrapEl = null;
    this.gridListEl = null;
    this.toolbarEl = null;
    this.searchInputEl = null;
    this.searchNoteEl = null;
    this.countEl = null;
    this.chipsEl = null;
    this.mobileToggleEl = null;
    this.overlayEl = null;

    this.originalAside = document.getElementById('main-collection-filters');
    this.originalGrid = document.getElementById('ProductGridContainer');
    // Dawn renders its own "Sort by" dropdown and product count in a separate
    // bar that sits OUTSIDE #main-collection-filters, so hiding the sidebar
    // alone left them on the page — that was the duplicate product count, and
    // it became a duplicate sort dropdown once we added our own.
    this.originalSort = document.querySelector('facet-filters-form.facets-vertical-sort');

    this.init();
  }

  CustomFilters.prototype.init = function () {
    var self = this;
    this.readEmbeddedChunks(); // picks up indexSectionId before anything fetches
    this.loadIndex().then(function (products) {
      if (!products || !products.length) return; // leave original Shopify grid/filters visible
      self.collectionProducts = prepareProducts(products, self);
      self.setPool(self.collectionProducts);
      self.applyUrlFilters();
      self.buildLayout();
      self.hideOriginal();
      self.update();
      self.root.hidden = false;
      // ?q=ping+g425 (the home page search box) fills in the search box and
      // runs the store-wide search straight away.
      var q = '';
      try { q = new URLSearchParams(window.location.search).get('q') || ''; } catch (e) { q = ''; }
      if (q.trim() && self.searchInputEl) {
        self.searchInputEl.value = q.trim();
        self.applySearch(q);
      }
    });
  };

  // Ticks filters named in the page address, so a link can land on results
  // that are already filtered — this is how the home page Shop menu works:
  //   /collections/all-clubs?club_type=Driver&handedness=Right-Handed&flex=Stiff&brand=Ping
  // Each name is a filter's internal name (see FILTER_FIELDS above) and each
  // value is the label shown in that filter. Repeat a name to tick several
  // (brand=Ping&brand=Titleist). Values this collection doesn't have are
  // ignored, and capitals don't matter.
  CustomFilters.prototype.applyUrlFilters = function () {
    var self = this;
    var params;
    try { params = new URLSearchParams(window.location.search); } catch (e) { return; }
    // ?sort=date-desc (or any key from SORT_OPTIONS) picks the sort order,
    // e.g. the home page's "See all" link under Just listed.
    // ?price_min=100&price_max=300 (dollars) sets the price range, e.g. from
    // the price boxes on the home page Shop menu's last step.
    var pMin = parseFloat(params.get('price_min')), pMax = parseFloat(params.get('price_max'));
    if (!isNaN(pMin) || !isNaN(pMax)) {
      this.state.price = { min: isNaN(pMin) ? null : Math.round(pMin * 100), max: isNaN(pMax) ? null : Math.round(pMax * 100) };
    }
    var sortWanted = params.get('sort');
    SORT_OPTIONS.forEach(function (o) { if (o.key === sortWanted) self.state.sort = o.key; });
    FILTER_FIELDS.forEach(function (f) {
      params.getAll(f.key).forEach(function (wanted) {
        var w = String(wanted).trim().toLowerCase();
        if (!w) return;
        (self.masterOptions[f.key] || []).forEach(function (option) {
          if (String(option).trim().toLowerCase() === w) self.state[f.key].add(option);
        });
      });
    });
  };

  // ---- Data loading -------------------------------------------------------

  CustomFilters.prototype.readEmbeddedChunks = function () {
    var self = this;
    var nodes = document.querySelectorAll('script[data-custom-filter-index]');
    var chunks = [];
    nodes.forEach(function (node) {
      // Remember the id Shopify actually gave this section, so the background
      // loads below ask for the right thing.
      var sid = node.getAttribute('data-section-id');
      if (sid) self.indexSectionId = sid;
      try {
        chunks.push({
          page: parseInt(node.getAttribute('data-page'), 10),
          totalPages: parseInt(node.getAttribute('data-total-pages'), 10),
          data: JSON.parse(node.textContent)
        });
      } catch (e) { /* skip malformed chunk */ }
    });
    return chunks;
  };

  // Fetches one page of a collection and pulls the index chunk out of it.
  // Returns { data: [...], totalPages: n } so the caller can discover how
  // many pages there are without a separate request.
  CustomFilters.prototype.fetchChunk = function (page, baseUrl) {
    var url = (baseUrl || this.collectionUrl) +
      '?section_id=' + encodeURIComponent(this.indexSectionId || INDEX_SECTION_ID) +
      '&page=' + page;
    return fetch(url)
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var node = doc.querySelector('script[data-custom-filter-index]');
        if (!node) return { data: [], totalPages: 1 };
        var total = parseInt(node.getAttribute('data-total-pages'), 10) || 1;
        try { return { data: JSON.parse(node.textContent), totalPages: total }; }
        catch (e) { return { data: [], totalPages: total }; }
      })
      .catch(function () { return { data: [], totalPages: 1 }; });
  };

  CustomFilters.prototype.readCache = function (cacheKey) {
    try {
      var raw = localStorage.getItem(cacheKey);
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      if (!parsed || !parsed.ts || !parsed.products || !parsed.products.length) return null;
      if (Date.now() - parsed.ts >= CACHE_TTL_MS) return null;
      return parsed.products;
    } catch (e) { return null; }
  };

  CustomFilters.prototype.writeCache = function (cacheKey, products) {
    try {
      localStorage.setItem(cacheKey, JSON.stringify({ ts: Date.now(), products: products }));
    } catch (e) {
      // Storage full (each collection caches its own index and browsers cap
      // this around 5MB). Drop every other cached index and try once more —
      // a slower page is better than no cache at all. If it still fails we
      // carry on uncached, which costs speed but nothing else.
      try {
        Object.keys(localStorage).forEach(function (k) {
          if (k.indexOf('cfidx:') === 0 && k !== cacheKey) localStorage.removeItem(k);
        });
        localStorage.setItem(cacheKey, JSON.stringify({ ts: Date.now(), products: products }));
      } catch (e2) { /* give up quietly */ }
    }
  };

  // Loads this collection's index. Page 1 is already embedded in the page by
  // the section, so only the remaining pages are fetched.
  CustomFilters.prototype.loadIndex = function () {
    var self = this;
    // The version number must be bumped whenever the fields stored per
    // product change, so returning shoppers don't keep using a stale index
    // that's missing the new field. v2 added created_at for date sorting.
    // v4: driving irons are labelled "Driving Iron" in club_type.
    var cacheKey = 'cfidx:v4:' + this.collectionUrl;
    var cached = this.readCache(cacheKey);

    var freshPromise = (function () {
      var chunks = self.readEmbeddedChunks();
      if (!chunks.length) return Promise.resolve([]);
      var totalPages = chunks[0].totalPages || 1;
      var have = {};
      var all = [];
      chunks.forEach(function (c) {
        have[c.page] = true;
        all = all.concat(c.data);
      });
      var fetches = [];
      for (var p = 1; p <= totalPages; p++) {
        if (!have[p]) fetches.push(self.fetchChunk(p, self.collectionUrl));
      }
      return Promise.all(fetches).then(function (results) {
        results.forEach(function (r) { all = all.concat(r.data); });
        self.writeCache(cacheKey, all);
        return all;
      });
    })();

    if (cached) {
      freshPromise.then(function (fresh) {
        if (fresh.length && fresh.length !== cached.length) {
          self.collectionProducts = prepareProducts(fresh, self);
          if (!self.searchQuery) {
            self.setPool(self.collectionProducts);
            self.update();
          }
        }
      });
      return Promise.resolve(cached);
    }

    return freshPromise;
  };

  // Loads every active product in the store, for the search box. Shopify
  // gives us /collections/all for free — it lists every published product
  // and renders with the same collection template, so our index chunks come
  // out of it exactly the same way. Called lazily the first time a shopper
  // touches the search box, so it costs nothing for shoppers who never do.
  CustomFilters.prototype.loadAllProducts = function () {
    var self = this;
    if (this.allState === 'ready' || this.allState === 'loading') return;

    // On /collections/all (where the header search box lands) this page's
    // own list already IS every product in the store — reuse it rather than
    // downloading the same thing a second time.
    if (this.collectionUrl === SEARCH_ALL_URL && this.collectionProducts && this.collectionProducts.length) {
      this.allProducts = this.collectionProducts;
      this.allState = 'ready';
      if (this.searchQuery) this.update();
      return;
    }

    var cacheKey = 'cfidx:v4:__all__';
    var cached = this.readCache(cacheKey);
    if (cached) {
      this.allProducts = prepareProducts(cached, this);
      this.allState = 'ready';
      if (this.searchQuery) this.update();
      return;
    }

    this.allState = 'loading';
    this.renderSearchNote();

    this.fetchChunk(1, SEARCH_ALL_URL).then(function (first) {
      if (!first.data.length) throw new Error('no index at ' + SEARCH_ALL_URL);
      var all = first.data.slice();
      var fetches = [];
      for (var p = 2; p <= (first.totalPages || 1); p++) {
        fetches.push(self.fetchChunk(p, SEARCH_ALL_URL));
      }
      return Promise.all(fetches).then(function (rest) {
        rest.forEach(function (r) { all = all.concat(r.data); });
        return all;
      });
    }).then(function (all) {
      self.writeCache(cacheKey, all);
      self.allProducts = prepareProducts(all, self);
      self.allState = 'ready';
      self.update();
    }).catch(function () {
      // Couldn't reach the store-wide list. Search still works, but only
      // across this collection — better than the box doing nothing.
      self.allState = 'failed';
      self.update();
    });
  };

  // ---- Derived data ---------------------------------------------------------

  // Works out the display value(s) this product contributes to each filter.
  // Split out from computeDerived so it can run once per product and be
  // reused when the pool changes (e.g. switching to store-wide search).
  CustomFilters.prototype.computeValues = function (p) {
    var self = this;
    p._values = {};
    FILTER_FIELDS.forEach(function (f) {
      var normalize = self.normalizers[f.key];
      if (f.key === 'brand') {
        var grouped = normalize(p.vendor);
        var values = [grouped];
        // A vendor that isn't in the curated brand list lands in "Other".
        // Carry its own tidied-up name alongside "Other" so the Brand search
        // box can offer it as a brand in its own right, without it cluttering
        // the list when nobody has searched for it. "Other" stays FIRST, so
        // the brand logo on the product card is unaffected.
        var extra = unlistedDisplay(f.key, p.vendor, grouped);
        if (extra) values.push(extra);
        p._values[f.key] = values;
      } else {
        var raw = p.mf ? p.mf[f.key] : null;
        p._values[f.key] = splitMultiValue(raw).map(normalize);
      }
    });
    return p._values;
  };

  // Swaps in a new set of products to filter — this collection normally, or
  // the store-wide list while a search is running — and rebuilds the filter
  // options and price bounds to match, so counts always describe what the
  // shopper is actually looking at.
  CustomFilters.prototype.setPool = function (list) {
    this.products = list || [];
    this.computeDerived();
  };

  CustomFilters.prototype.computeDerived = function () {
    var self = this;
    var masterSets = {};
    FILTER_FIELDS.forEach(function (f) { masterSets[f.key] = new Set(); });

    var minPrice = null, maxPrice = null;

    this.products.forEach(function (p) {
      if (!p._values) self.computeValues(p);
      FILTER_FIELDS.forEach(function (f) {
        p._values[f.key].forEach(function (v) { masterSets[f.key].add(v); });
      });
      if (typeof p.price === 'number') {
        if (minPrice === null || p.price < minPrice) minPrice = p.price;
        if (maxPrice === null || p.price > maxPrice) maxPrice = p.price;
      }
    });

    this.masterOptions = {};
    FILTER_FIELDS.forEach(function (f) {
      self.masterOptions[f.key] = sortFieldOptions(f.key, Array.from(masterSets[f.key]));
    });
    this.priceBounds = { min: minPrice || 0, max: maxPrice || 0 };
  };

  // ---- Filtering logic --------------------------------------------------

  CustomFilters.prototype.productMatches = function (product, excludeKey) {
    for (var i = 0; i < FILTER_FIELDS.length; i++) {
      var f = FILTER_FIELDS[i];
      if (f.key === excludeKey) continue;
      var sel = this.state[f.key];
      if (sel.size === 0) continue;
      var vals = product._values[f.key];
      var hit = vals.some(function (v) { return sel.has(v); });
      if (!hit) return false;
    }
    if (excludeKey !== 'price') {
      var pr = this.state.price;
      if (pr.min != null && product.price < pr.min) return false;
      if (pr.max != null && product.price > pr.max) return false;
    }
    return true;
  };

  CustomFilters.prototype.getFiltered = function (excludeKey) {
    var self = this;
    return this.products.filter(function (p) { return self.productMatches(p, excludeKey); });
  };

  CustomFilters.prototype.countsFor = function (fieldKey) {
    var subset = this.getFiltered(fieldKey);
    var counts = {};
    subset.forEach(function (p) {
      p._values[fieldKey].forEach(function (v) { counts[v] = (counts[v] || 0) + 1; });
    });
    return counts;
  };

  // ---- Layout / DOM setup -------------------------------------------------

  CustomFilters.prototype.buildLayout = function () {
    var self = this;

    var wrap = document.createElement('div');
    wrap.className = 'custom-filters__layout';

    // Everything above the products: search, the Filter/Sort row, and the
    // active-filter chips. Built ONCE and then updated in place — if this
    // were re-rendered like the grid is, the search box would lose focus
    // and drop characters on every keystroke.
    var topbar = document.createElement('div');
    topbar.className = 'custom-filters__topbar';
    topbar.innerHTML =
      '<div class="custom-filters__search">' +
        ICON_SEARCH +
        '<input type="search" class="custom-filters__search-input" ' +
          'placeholder="Search all clubs" aria-label="Search all products" ' +
          'autocomplete="off" autocorrect="off" spellcheck="false">' +
        '<button type="button" class="custom-filters__search-clear" data-search-clear ' +
          'aria-label="Clear search" hidden>&times;</button>' +
      '</div>' +
      '<p class="custom-filters__search-note" data-search-note hidden></p>' +
      '<div class="custom-filters__controls">' +
        '<button type="button" class="custom-filters__filter-btn" data-open-drawer>' +
          ICON_FILTER + '<span>Filter</span>' +
          '<span class="custom-filters__badge" data-filter-badge hidden></span>' +
        '</button>' +
        '<div class="custom-filters__result-count" role="status" aria-live="polite"></div>' +
        this.renderSortSelect() +
      '</div>' +
      // The applied filters, then "Save this search" right after "Clear all"
      // (assets/cp-alerts.js). The whole row only shows once something is
      // ticked or searched, so it's clear the button saves the FILTERS.
      '<div class="custom-filters__chips-row" data-chips-row hidden>' +
        '<div class="custom-filters__chips" data-chips hidden></div>' +
        (window.CPAlerts
          ? '<button type="button" class="cp-save-search" data-save-search>' + window.CPAlerts.bookmarkSvg +
              '<span>Save this search</span></button>'
          : '') +
      '</div>';
    wrap.appendChild(topbar);

    var body = document.createElement('div');
    body.className = 'custom-filters__body';
    wrap.appendChild(body);

    var sidebar = document.createElement('aside');
    sidebar.className = 'custom-filters__sidebar';
    body.appendChild(sidebar);

    var gridWrap = document.createElement('div');
    gridWrap.className = 'custom-filters__grid-wrap';
    body.appendChild(gridWrap);

    var overlay = document.createElement('div');
    overlay.className = 'custom-filters__overlay';
    this.root.appendChild(overlay);

    this.root.appendChild(wrap);

    this.sidebarEl = sidebar;
    this.gridWrapEl = gridWrap;
    this.toolbarEl = topbar;
    this.overlayEl = overlay;
    this.searchInputEl = topbar.querySelector('.custom-filters__search-input');
    this.searchClearEl = topbar.querySelector('[data-search-clear]');
    this.searchNoteEl = topbar.querySelector('[data-search-note]');
    this.countEl = topbar.querySelector('.custom-filters__result-count');
    this.chipsEl = topbar.querySelector('[data-chips]');
    this.chipsRowEl = topbar.querySelector('[data-chips-row]');
    this.badgeEl = topbar.querySelector('[data-filter-badge]');
    this.mobileToggleEl = topbar.querySelector('[data-open-drawer]');

    // ---- Search --------------------------------------------------------
    // Start pulling the store-wide product list as soon as the shopper shows
    // interest, so it's usually ready by the time they finish their first word.
    var warm = function () { self.loadAllProducts(); };
    this.searchInputEl.addEventListener('focus', warm, { once: true });

    this.searchInputEl.addEventListener('input', debounce(function () {
      self.applySearch(self.searchInputEl.value);
    }, SEARCH_DEBOUNCE_MS));

    // Enter shouldn't submit anything or reload the page — results are live.
    this.searchInputEl.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') {
        e.preventDefault();
        self.applySearch(self.searchInputEl.value);
      }
      if (e.key === 'Escape' && self.searchInputEl.value) {
        e.preventDefault();
        self.clearSearch();
      }
    });

    this.searchClearEl.addEventListener('click', function () { self.clearSearch(); });

    // ---- Save this search (assets/cp-alerts.js does the saving) --------
    var saveBtn = topbar.querySelector('[data-save-search]');
    if (saveBtn) saveBtn.addEventListener('click', function () {
      window.CPAlerts.saveSearch({
        url: self.currentSearchUrl(),
        collectionTitle: String(document.title || '').split(/\s[–|-]\s/)[0]
      }, saveBtn);
    });

    // ---- Toolbar -------------------------------------------------------
    topbar.addEventListener('change', function (e) {
      if (e.target.matches('[data-sort-select]')) {
        self.state.sort = e.target.value;
        self.update();
      }
    });

    this.mobileToggleEl.addEventListener('click', function () { self.toggleMobile(true); });

    topbar.addEventListener('click', function (e) {
      var chip = e.target.closest('[data-chip-field]');
      if (chip) {
        self.state[chip.getAttribute('data-chip-field')].delete(chip.getAttribute('data-chip-value'));
        self.visibleCount = self.pageSize;
        self.update();
        return;
      }
      if (e.target.closest('[data-chip-price]')) {
        self.state.price = { min: null, max: null };
        self.visibleCount = self.pageSize;
        self.update();
        return;
      }
      if (e.target.closest('[data-chip-search]')) { self.clearSearch(); return; }
      if (e.target.closest('[data-clear-all]')) { self.clearAll(); }
    });

    // ---- Sidebar -------------------------------------------------------
    overlay.addEventListener('click', function () { self.toggleMobile(false); });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && self.sidebarEl.classList.contains('is-open')) {
        self.toggleMobile(false);
      }
    });

    // Swipe the drawer away to the left, the way a native drawer behaves.
    var touchStartX = null, touchStartY = null;
    sidebar.addEventListener('touchstart', function (e) {
      if (e.touches.length !== 1) return;
      touchStartX = e.touches[0].clientX;
      touchStartY = e.touches[0].clientY;
    }, { passive: true });
    sidebar.addEventListener('touchend', function (e) {
      if (touchStartX === null) return;
      var t = e.changedTouches[0];
      var dx = t.clientX - touchStartX;
      var dy = Math.abs(t.clientY - touchStartY);
      // Mostly-sideways swipe of a decent length, so scrolling a long filter
      // list doesn't accidentally shut the drawer.
      if (dx < -60 && dy < Math.abs(dx)) self.toggleMobile(false);
      touchStartX = null;
      touchStartY = null;
    }, { passive: true });

    sidebar.addEventListener('change', function (e) {
      var el = e.target;
      if (el.matches('input[type="checkbox"][data-field]')) {
        var field = el.getAttribute('data-field');
        var val = el.getAttribute('data-value');
        if (el.checked) self.state[field].add(val);
        else self.state[field].delete(val);
        self.visibleCount = self.pageSize;
        self.update();
      }
    });

    sidebar.addEventListener('toggle', function (e) {
      if (e.target.matches('details[data-field]')) {
        self.openGroups[e.target.getAttribute('data-field')] = e.target.open;
      }
    }, true);

    // Typing in a filter's own search box (Brand). Only that filter's list of
    // options is redrawn, and the box itself is left alone so it keeps focus.
    // Nothing about the products changes here, so the grid isn't touched.
    sidebar.addEventListener('input', function (e) {
      var box = e.target;
      if (!box.getAttribute || !box.getAttribute('data-option-search')) return;
      var key = box.getAttribute('data-option-search');
      self.optionQuery[key] = box.value || '';
      self.refreshFieldRows(key);
    });

    // Escape clears the box rather than shutting the whole drawer.
    sidebar.addEventListener('keydown', function (e) {
      var box = e.target;
      if (!box.getAttribute || !box.getAttribute('data-option-search')) return;
      if (e.key === 'Enter') { e.preventDefault(); return; }
      if (e.key !== 'Escape') return;
      // Nothing typed? Let Escape through to do its usual job of closing the
      // mobile drawer.
      if (!box.value) return;
      e.preventDefault();
      e.stopPropagation();
      var key = box.getAttribute('data-option-search');
      box.value = '';
      self.optionQuery[key] = '';
      self.refreshFieldRows(key);
    });

    sidebar.addEventListener('input', debounce(function (e) {
      if (e.target.matches('input[data-price]')) {
        self.readPriceInputs();
        self.visibleCount = self.pageSize;
        self.update();
      }
    }, 300));

    sidebar.addEventListener('click', function (e) {
      if (e.target.closest('[data-close-drawer]')) {
        e.preventDefault();
        self.toggleMobile(false);
        return;
      }
      if (e.target.closest('[data-clear-all]')) {
        e.preventDefault();
        self.clearAll();
        return;
      }
      var clearBtn = e.target.closest('[data-option-search-clear]');
      if (clearBtn) {
        e.preventDefault();
        var key = clearBtn.getAttribute('data-option-search-clear');
        self.optionQuery[key] = '';
        var box = self.sidebarEl.querySelector('[data-option-search="' + key + '"]');
        if (box) { box.value = ''; box.focus(); }
        self.refreshFieldRows(key);
      }
    });

    // ---- Grid ----------------------------------------------------------
    gridWrap.addEventListener('click', function (e) {
      if (e.target.closest('[data-load-more]')) {
        self.visibleCount += self.pageSize;
        self.renderGrid(self.lastFiltered);
      }
      if (e.target.closest('[data-clear-all]')) self.clearAll();
      if (e.target.closest('[data-clear-search]')) self.clearSearch();
    });
  };

  CustomFilters.prototype.clearAll = function () {
    var self = this;
    FILTER_FIELDS.forEach(function (f) { self.state[f.key].clear(); });
    this.optionQuery = {};
    this.state.price = { min: null, max: null };
    this.visibleCount = this.pageSize;
    this.update();
  };

  CustomFilters.prototype.clearSearch = function () {
    this.searchQuery = '';
    if (this.searchInputEl) this.searchInputEl.value = '';
    this.visibleCount = this.pageSize;
    this.update();
  };

  // The address of exactly what's on screen: this collection, every ticked
  // filter, the price range and the search words. Same parameters
  // applyUrlFilters() reads, so a saved search reopens as it was saved.
  CustomFilters.prototype.currentSearchUrl = function () {
    var self = this;
    var qs = [];
    var add = function (k, v) { qs.push(encodeURIComponent(k) + '=' + encodeURIComponent(v)); };
    FILTER_FIELDS.forEach(function (f) { self.state[f.key].forEach(function (v) { add(f.key, v); }); });
    if (this.searchQuery) add('q', this.searchQuery);
    if (this.state.price.min != null) add('price_min', Math.round(this.state.price.min / 100));
    if (this.state.price.max != null) add('price_max', Math.round(this.state.price.max / 100));
    return this.collectionUrl + (qs.length ? '?' + qs.join('&') : '');
  };

  CustomFilters.prototype.applySearch = function (raw) {
    var q = String(raw == null ? '' : raw).trim();
    if (q === this.searchQuery) return;
    this.searchQuery = q;
    this.visibleCount = this.pageSize;
    if (q) this.loadAllProducts();
    this.update();
  };

  // Chooses what's being filtered right now: this collection when the search
  // box is empty, otherwise every active product in the store that matches
  // what was typed. Falls back to searching just this collection if the
  // store-wide list hasn't arrived (or couldn't be fetched).
  CustomFilters.prototype.refreshPool = function () {
    if (!this.searchQuery) {
      if (this.products !== this.collectionProducts) this.setPool(this.collectionProducts);
      return;
    }
    var source = (this.allState === 'ready' && this.allProducts)
      ? this.allProducts
      : this.collectionProducts;
    var tokens = searchTokens(this.searchQuery);
    this.setPool(source.filter(function (p) { return matchesSearch(p, tokens); }));
  };

  CustomFilters.prototype.activeFilterCount = function () {
    var self = this;
    var n = 0;
    FILTER_FIELDS.forEach(function (f) { n += self.state[f.key].size; });
    if (this.state.price.min != null || this.state.price.max != null) n += 1;
    return n;
  };

  CustomFilters.prototype.toggleMobile = function (open) {
    this.sidebarEl.classList.toggle('is-open', open);
    this.overlayEl.classList.toggle('is-open', open);
    document.body.style.overflow = open ? 'hidden' : '';
    this.mobileToggleEl.setAttribute('aria-expanded', open ? 'true' : 'false');
    if (open) {
      var closeBtn = this.sidebarEl.querySelector('[data-close-drawer]');
      if (closeBtn) closeBtn.focus();
    } else {
      this.mobileToggleEl.focus();
    }
  };

  CustomFilters.prototype.hideOriginal = function () {
    if (this.originalAside) this.originalAside.style.display = 'none';
    if (this.originalGrid) this.originalGrid.style.display = 'none';
    if (this.originalSort) this.originalSort.style.display = 'none';
  };

  CustomFilters.prototype.readPriceInputs = function () {
    var minEl = this.sidebarEl.querySelector('[data-price="min"]');
    var maxEl = this.sidebarEl.querySelector('[data-price="max"]');
    var min = minEl && minEl.value !== '' ? Math.round(parseFloat(minEl.value) * 100) : null;
    var max = maxEl && maxEl.value !== '' ? Math.round(parseFloat(maxEl.value) * 100) : null;
    this.state.price = { min: isNaN(min) ? null : min, max: isNaN(max) ? null : max };
  };

  // ---- Rendering ----------------------------------------------------------

  CustomFilters.prototype.update = function () {
    this.refreshPool();
    var filtered = this.applySort(this.getFiltered(null));
    this.lastFiltered = filtered;
    this.renderSidebar();
    this.renderToolbar(filtered.length);
    this.renderGrid(filtered);
  };

  // ---- Toolbar (persistent — updated in place, never re-created) ---------

  CustomFilters.prototype.renderToolbar = function (count) {
    var n = this.activeFilterCount();

    this.countEl.textContent = count + (count === 1 ? ' product' : ' products');

    if (n > 0) {
      this.badgeEl.textContent = n;
      this.badgeEl.hidden = false;
    } else {
      this.badgeEl.hidden = true;
    }

    this.searchClearEl.hidden = !this.searchQuery;
    this.renderSearchNote();
    this.renderChips();
  };

  CustomFilters.prototype.renderSearchNote = function () {
    if (!this.searchNoteEl) return;
    var note = '';
    if (this.searchQuery && this.allState === 'loading') {
      note = 'Searching all products…';
    } else if (this.searchQuery && this.allState === 'ready') {
      note = 'Showing matches from all products, not just this page.';
    } else if (this.searchQuery && this.allState === 'failed') {
      note = 'Showing matches from this page only — the full product list didn\'t load.';
    }
    this.searchNoteEl.textContent = note;
    this.searchNoteEl.hidden = !note;
  };

  // A removable pill for every choice the shopper has made, so they can see
  // and undo what's applied without opening the filter drawer again.
  CustomFilters.prototype.renderChips = function () {
    var self = this;
    var chips = [];

    if (this.searchQuery) {
      chips.push(
        '<button type="button" class="custom-filters__chip" data-chip-search>' +
        '<span>Search: ' + escapeHtml(this.searchQuery) + '</span>' +
        '<span class="custom-filters__chip-x" aria-hidden="true">&times;</span>' +
        '<span class="visually-hidden">Clear search</span></button>'
      );
    }

    FILTER_FIELDS.forEach(function (f) {
      self.state[f.key].forEach(function (val) {
        chips.push(
          '<button type="button" class="custom-filters__chip" data-chip-field="' +
          escapeHtml(f.key) + '" data-chip-value="' + escapeHtml(val) + '">' +
          '<span>' + escapeHtml(val) + '</span>' +
          '<span class="custom-filters__chip-x" aria-hidden="true">&times;</span>' +
          '<span class="visually-hidden">Remove ' + escapeHtml(fieldLabel(f.key)) +
          ' ' + escapeHtml(val) + '</span></button>'
        );
      });
    });

    var pr = this.state.price;
    if (pr.min != null || pr.max != null) {
      var label = (pr.min != null ? '$' + (pr.min / 100).toFixed(0) : 'Any') + ' – ' +
        (pr.max != null ? '$' + (pr.max / 100).toFixed(0) : 'Any');
      chips.push(
        '<button type="button" class="custom-filters__chip" data-chip-price>' +
        '<span>' + escapeHtml(label) + '</span>' +
        '<span class="custom-filters__chip-x" aria-hidden="true">&times;</span>' +
        '<span class="visually-hidden">Remove price range</span></button>'
      );
    }

    if (chips.length > 1) {
      chips.push('<button type="button" class="custom-filters__chip-clear" data-clear-all>Clear all</button>');
    }

    this.chipsEl.innerHTML = chips.join('');
    this.chipsEl.hidden = chips.length === 0;
    if (this.chipsRowEl) {
      this.chipsRowEl.hidden = chips.length === 0;
      // Different filters = a different search, not saved yet.
      var sb = this.chipsRowEl.querySelector('[data-save-search]');
      if (sb) sb.classList.remove('is-saved');
    }
  };

  // ---- Sidebar ----------------------------------------------------------

  CustomFilters.prototype.renderSidebar = function () {
    var self = this;
    var anyActive = this.activeFilterCount() > 0;
    var count = this.lastFiltered ? this.lastFiltered.length : 0;

    // The close button and the "Show N products" button at the bottom are
    // both mobile-only (hidden by CSS on desktop, where the sidebar is just
    // a column). Between them, the dark overlay and swiping left, there are
    // four separate ways out of the drawer on a phone.
    var html = '<div class="custom-filters__header">' +
      '<h2 class="custom-filters__heading h2">Filter</h2>' +
      '<button type="button" class="custom-filters__close" data-close-drawer ' +
      'aria-label="Close filters">&times;</button>' +
      '</div>';

    if (anyActive) {
      html += '<div class="custom-filters__clear-row">' +
        '<button type="button" class="custom-filters__clear link underlined-link" data-clear-all>Clear all</button>' +
        '</div>';
    }

    FILTER_FIELDS.forEach(function (f) {
      html += self.renderFieldGroup(f);
    });

    html += this.renderPriceGroup();

    html += '<div class="custom-filters__drawer-foot">' +
      '<button type="button" class="button custom-filters__apply" data-close-drawer>Show ' +
      count + (count === 1 ? ' product' : ' products') + '</button></div>';

    // If the shopper was typing in a filter's search box when something else
    // forced the whole sidebar to redraw (ticking a box, say), put the cursor
    // back where it was — otherwise the next keystroke would go nowhere.
    var active = document.activeElement;
    var refocusField = active && active.getAttribute
      ? active.getAttribute('data-option-search')
      : null;

    this.sidebarEl.innerHTML = html;

    if (refocusField) {
      var box = this.sidebarEl.querySelector('[data-option-search="' + refocusField + '"]');
      if (box) {
        box.focus();
        try { box.setSelectionRange(box.value.length, box.value.length); } catch (err) {}
      }
    }
  };

  CustomFilters.prototype.renderFieldGroup = function (field) {
    var options = this.masterOptions[field.key] || [];
    if (!options.length) return '';
    var selected = this.state[field.key];
    // Open/closed state, in priority order:
    //  1. Whatever the shopper last set by clicking the heading. Kept in
    //     this.openGroups so it survives the sidebar re-render that happens
    //     on every selection change — otherwise filters would snap shut on
    //     each click.
    //  2. Otherwise, open if this filter already has something selected
    //     (e.g. someone opened a shared, pre-filtered link).
    //  3. Otherwise, the "startOpen" flag in custom-filter-config.js.
    //     Filters without that flag start collapsed.
    var isOpen = Object.prototype.hasOwnProperty.call(this.openGroups, field.key)
      ? this.openGroups[field.key]
      : (selected.size > 0 || fieldConfig(field.key).startOpen === true);

    return (
      '<details class="custom-filters__group" data-field="' + field.key + '"' + (isOpen ? ' open' : '') + '>' +
      '<summary class="custom-filters__summary">' + escapeHtml(fieldLabel(field.key)) + '</summary>' +
      this.renderFieldSearch(field.key) +
      '<ul class="custom-filters__list list-unstyled" data-option-list="' + escapeHtml(field.key) + '">' +
      this.renderFieldRows(field.key) +
      '</ul>' +
      '</details>'
    );
  };

  // The search box that sits inside a filter, between its heading and its
  // list of options. Only appears on filters with "search: true" in
  // custom-filter-config.js. It lives OUTSIDE the <ul> on purpose: typing
  // replaces only the list, never the box, so it keeps focus and can't drop
  // a character.
  CustomFilters.prototype.renderFieldSearch = function (fieldKey) {
    var cfg = fieldConfig(fieldKey);
    if (!cfg.search) return '';
    var query = this.optionQuery[fieldKey] || '';
    var label = fieldLabel(fieldKey);
    var placeholder = cfg.searchPlaceholder || ('Search ' + label.toLowerCase());
    return (
      '<div class="custom-filters__option-search">' +
      ICON_SEARCH +
      '<input type="search" class="custom-filters__option-search-input" ' +
      'data-option-search="' + escapeHtml(fieldKey) + '" ' +
      'value="' + escapeHtml(query) + '" ' +
      'placeholder="' + escapeHtml(placeholder) + '" ' +
      'autocomplete="off" spellcheck="false" ' +
      'aria-label="Search ' + escapeHtml(label) + '">' +
      (query
        ? '<button type="button" class="custom-filters__option-search-clear" ' +
          'data-option-search-clear="' + escapeHtml(fieldKey) + '" aria-label="Clear ' +
          escapeHtml(label) + ' search">&times;</button>'
        : '') +
      '</div>'
    );
  };

  // Just the checkbox rows for one filter. Split out from renderFieldGroup so
  // typing in that filter's search box can redraw the list on its own.
  CustomFilters.prototype.renderFieldRows = function (fieldKey) {
    var options = this.masterOptions[fieldKey] || [];
    var counts = this.countsFor(fieldKey);
    var selected = this.state[fieldKey];
    var query = this.optionQuery[fieldKey] || '';
    var tokens = queryTokens(query);
    var searching = tokens.length > 0;
    var hideUnlisted = hidesUnlisted(fieldKey);

    // Which options belong in the list at all:
    //   - anything already ticked always stays, so a choice can never be
    //     hidden by what's typed in the box;
    //   - while searching, whatever matches — including the raw Shopify
    //     values that normally sit inside "Other";
    //   - otherwise the curated list from custom-filter-config.js.
    options = options.filter(function (val) {
      if (selected.has(val)) return true;
      if (searching) return optionMatchesQuery(fieldKey, val, tokens);
      return hideUnlisted ? isListedValue(fieldKey, val) : true;
    });

    // Reorder the list so a shopper sees what's actually available first.
    // Three tiers, in this order:
    //   1. Anything they've already ticked, so their choices stay in view
    //      even on a long list like Brand.
    //   2. Options that still have matching products.
    //   3. Options with no matches, greyed out at the bottom.
    // Within each tier the configured order from custom-filter-config.js is
    // preserved (including the pinned "Other"), so e.g. Club Number stays in
    // golf order among the values that are still available.
    var picked = [], available = [], empty = [];
    options.forEach(function (val) {
      if (selected.has(val)) picked.push(val);
      else if ((counts[val] || 0) > 0) available.push(val);
      else empty.push(val);
    });
    options = picked.concat(available, empty);

    var rows = options.map(function (val) {
      var count = counts[val] || 0;
      var isSelected = selected.has(val);
      var disabled = count === 0 && !isSelected;
      var id = 'cf-' + fieldKey + '-' + slugify(val);
      return (
        '<li class="custom-filters__option' + (disabled ? ' is-zero' : '') + '">' +
        '<label class="custom-filters__label" for="' + id + '">' +
        '<input type="checkbox" id="' + id + '" data-field="' + fieldKey + '" data-value="' +
        escapeHtml(val) + '"' + (isSelected ? ' checked' : '') + (disabled ? ' disabled' : '') + '>' +
        '<span class="custom-filters__option-text">' + escapeHtml(val) + '</span>' +
        '<span class="custom-filters__count">(' + count + ')</span>' +
        '</label></li>'
      );
    }).join('');

    if (!rows) {
      return '<li class="custom-filters__option-none">No matches for \u201c' +
        escapeHtml(query) + '\u201d</li>';
    }
    return rows;
  };

  // Redraws one filter's option list in place, and shows or hides its little
  // clear "x" to match. Deliberately leaves the search box element alone.
  CustomFilters.prototype.refreshFieldRows = function (fieldKey) {
    if (!this.sidebarEl) return;
    var list = this.sidebarEl.querySelector('[data-option-list="' + fieldKey + '"]');
    if (list) list.innerHTML = this.renderFieldRows(fieldKey);

    var wrap = this.sidebarEl.querySelector('[data-option-search="' + fieldKey + '"]');
    wrap = wrap ? wrap.parentNode : null;
    if (!wrap) return;
    var clear = wrap.querySelector('[data-option-search-clear]');
    var hasQuery = !!(this.optionQuery[fieldKey] || '');
    if (hasQuery && !clear) {
      clear = document.createElement('button');
      clear.type = 'button';
      clear.className = 'custom-filters__option-search-clear';
      clear.setAttribute('data-option-search-clear', fieldKey);
      clear.setAttribute('aria-label', 'Clear ' + fieldLabel(fieldKey) + ' search');
      clear.innerHTML = '&times;';
      wrap.appendChild(clear);
    } else if (!hasQuery && clear) {
      wrap.removeChild(clear);
    }
  };

  CustomFilters.prototype.renderPriceGroup = function () {
    // Same open/closed rules as the other filters — see renderFieldGroup.
    var priceActive = this.state.price.min != null || this.state.price.max != null;
    var isOpen = Object.prototype.hasOwnProperty.call(this.openGroups, 'price')
      ? this.openGroups.price
      : (priceActive || fieldConfig('price').startOpen === true);
    var min = this.state.price.min != null ? (this.state.price.min / 100).toFixed(2) : '';
    var max = this.state.price.max != null ? (this.state.price.max / 100).toFixed(2) : '';
    var boundsMin = (this.priceBounds.min / 100).toFixed(2);
    var boundsMax = (this.priceBounds.max / 100).toFixed(2);
    return (
      '<details class="custom-filters__group" data-field="price"' + (isOpen ? ' open' : '') + '>' +
      '<summary class="custom-filters__summary">' + escapeHtml(fieldConfig('price').label || 'Price') + '</summary>' +
      '<div class="custom-filters__price-row">' +
      '<label class="custom-filters__price-field">Min<input type="number" inputmode="decimal" data-price="min" placeholder="$' + boundsMin + '" value="' + min + '"></label>' +
      '<span class="custom-filters__price-sep">–</span>' +
      '<label class="custom-filters__price-field">Max<input type="number" inputmode="decimal" data-price="max" placeholder="$' + boundsMax + '" value="' + max + '"></label>' +
      '</div>' +
      '</details>'
    );
  };

  // Reorders the already-filtered list for the "Sort by" dropdown.
  // "featured" leaves the collection's own order alone. Ties keep their
  // existing relative order.
  CustomFilters.prototype.applySort = function (list) {
    var key = this.state.sort;
    if (!key || key === 'featured') return list;
    return list.slice().sort(function (a, b) {
      switch (key) {
        case 'price-asc':  return (a.price || 0) - (b.price || 0);
        case 'price-desc': return (b.price || 0) - (a.price || 0);
        case 'date-desc':  return (b.created_at || 0) - (a.created_at || 0);
        case 'date-asc':   return (a.created_at || 0) - (b.created_at || 0);
        default:           return 0;
      }
    });
  };

  CustomFilters.prototype.renderSortSelect = function () {
    var self = this;
    var id = 'cf-sort-' + slugify(this.sectionId || 'main');
    var opts = SORT_OPTIONS.map(function (o) {
      return '<option value="' + escapeHtml(o.key) + '"' +
        (self.state.sort === o.key ? ' selected' : '') + '>' + escapeHtml(o.label) + '</option>';
    }).join('');
    return (
      '<div class="custom-filters__sort">' +
      '<label class="custom-filters__sort-label" for="' + id + '">Sort by</label>' +
      '<select class="custom-filters__sort-select" id="' + id + '" data-sort-select>' + opts + '</select>' +
      '</div>'
    );
  };

  CustomFilters.prototype.renderGrid = function (filtered) {
    var visible = filtered.slice(0, this.visibleCount);

    if (!filtered.length) {
      // Two different dead ends need two different ways out: a search that
      // found nothing, versus filters that ruled everything out.
      var message, action;
      if (this.searchQuery && this.allState === 'loading') {
        message = 'Searching…';
        action = '';
      } else if (this.searchQuery) {
        message = 'Nothing matches &ldquo;' + escapeHtml(this.searchQuery) + '&rdquo;.';
        action = '<button type="button" class="link underlined-link" data-clear-search>Clear search</button>';
      } else {
        message = 'No products match these filters.';
        action = '<button type="button" class="link underlined-link" data-clear-all>Clear all filters</button>';
      }
      this.gridWrapEl.innerHTML =
        '<div class="custom-filters__empty"><p class="h2">' + message + '</p>' + action + '</div>';
      return;
    }

    var cardsHtml = visible.map(function (p) { return CustomFilters.renderCard(p); }).join('');
    var loadMore = filtered.length > visible.length
      ? '<div class="custom-filters__load-more-wrap"><button type="button" class="button button--secondary" data-load-more>Load more (' +
        (filtered.length - visible.length) + ' more)</button></div>'
      : '';

    // Dawn's flex-based grid classes let a lone result stretch or shrink to
    // fill the row, so one product came out as a tiny card. We use our own
    // CSS grid instead (see component-custom-filters.css) with the column
    // counts passed in from the section settings, so every card is the same
    // width whether there's 1 result or 1,000.
    var gridStyle = ' style="--cf-cols-desktop:' + this.columnsDesktop +
      ';--cf-cols-mobile:' + this.columnsMobile + ';"';

    this.gridWrapEl.innerHTML =
      '<ul class="product-grid custom-filters__grid"' + gridStyle + '>' + cardsHtml + '</ul>' +
      loadMore;
  };

  CustomFilters.renderCard = function (p) {
    var comparePart = p.compare_at_price_formatted
      ? '<s class="price-item price-item--regular">' + escapeHtml(p.compare_at_price_formatted) + '</s> '
      : '';
    // Clearance bubble on the photo, same rule as the theme's own cards:
    // shown whenever there's a crossed-out original price.
    var badgePart = p.compare_at_price_formatted
      ? '<div class="custom-filters__card-badge"><span class="badge color-' + SALE_BADGE_SCHEME + '">' + SALE_BADGE_TEXT + '</span></div>'
      : '';
    // Watch-list heart, top-right of the photo (assets/cp-alerts.js).
    var heartPart = window.CPAlerts
      ? '<button type="button" class="cp-heart" data-watch="' + escapeHtml(String(p.id)) + '"' +
          ' data-title="' + escapeHtml(p.title) + '" data-url="' + escapeHtml(p.url) + '"' +
          ' data-image="' + escapeHtml(p.image || '') + '" data-price="' + escapeHtml(String(p.price || 0)) + '"' +
          ' aria-pressed="' + (window.CPAlerts.isWatched(p.id) ? 'true' : 'false') + '" aria-label="Watch this club">' +
          window.CPAlerts.heartSvg + '</button>'
      : '';
    var vendorPart = p.vendor
      ? '<div class="caption-with-letter-spacing light">' + escapeHtml(p.vendor) + '</div>'
      : '';
    // Brand logo, bottom-right of the card. Uses the SAME brand name the
    // Brand filter shows, so every spelling variant and sub-brand grouping
    // in the config (e.g. "King Cobra" -> Cobra) gets the right logo.
    // Purely decorative: the brand is already written out above in the
    // vendor line, so alt is empty and screen readers skip it.
    var brand = p._values && p._values.brand ? p._values.brand[0] : '';
    var logoFile = brandLogoFile(brand);
    var logoPart = logoFile
      ? '<img class="custom-filters__card-logo" src="' + escapeHtml(ASSET_BASE + logoFile) + '" alt="" loading="lazy" decoding="async">'
      : '';

    // Extra-photo browsing. img_count comes from the index; anything with one
    // photo (or an older cached record with no count at all) gets the plain
    // card it has always had.
    var photoCount = Math.min(parseInt(p.img_count, 10) || 0, MAX_CARD_PHOTOS);
    var hasPhotos = photoCount > 1;
    var photoAttrs = '';
    var arrowsPart = '';
    var dotsPart = '';
    if (hasPhotos) {
      photoAttrs = ' data-card-photos data-handle="' + escapeHtml(handleFromUrl(p.url)) + '"' +
        ' data-photo-index="0" data-original-photo="' + escapeHtml(p.image || '') + '"';
      arrowsPart =
        '<button type="button" class="cf-card-arrow cf-card-arrow--prev" data-card-arrow="prev" aria-label="Previous photo">' + CARD_CARET_PREV + '</button>' +
        '<button type="button" class="cf-card-arrow cf-card-arrow--next" data-card-arrow="next" aria-label="Next photo">' + CARD_CARET_NEXT + '</button>';
      var dotsInner = '';
      for (var dot = 0; dot < photoCount; dot++) {
        dotsInner += '<span class="cf-card-dot' + (dot === 0 ? ' is-current' : '') + '"></span>';
      }
      dotsPart = '<div class="cf-card-dots" data-card-dots aria-hidden="true">' + dotsInner + '</div>';
    }
    return (
      '<li class="grid__item custom-filters__item">' +
      '<div class="card-wrapper product-card-wrapper underline-links-hover">' +
      '<div class="card card--card custom-filters__card"' + photoAttrs + '>' +
      '<div class="card__inner ratio" style="--ratio-percent:100%;">' +
      '<div class="card__media"><div class="media media--transparent">' +
      '<img src="' + escapeHtml(p.image || '') + '" alt="' + escapeHtml(p.title) + '" width="400" height="400" loading="lazy" class="motion-reduce" data-card-image>' +
      '</div></div>' +
      arrowsPart + dotsPart + badgePart + heartPart +
      '</div>' +
      '<div class="card__content"><div class="card__information">' +
      '<h3 class="card__heading h5"><a href="' + escapeHtml(p.url) + '" class="full-unstyled-link">' + escapeHtml(p.title) + '</a></h3>' +
      '<div class="card-information">' + vendorPart +
      '<div class="price"><span class="price-item price-item--regular">' + comparePart + escapeHtml(p.price_formatted) + '</span></div>' +
      '</div></div>' + logoPart + '</div>' +
      '</div></div></li>'
    );
  };

  // ---- Card photo browser -------------------------------------------------
  //
  // Lets a shopper step through a club's other photos without leaving the
  // collection page.
  //
  // The photo ADDRESSES are deliberately NOT in the product index — only the
  // count is. Putting every address in the index would have added hundreds of
  // kilobytes to every collection page for all ~1,100 clubs, whether or not
  // anyone used it. Instead the first time a card is hovered we ask Shopify
  // for that one product and remember the answer for the rest of the visit,
  // so a card nobody hovers costs nothing at all.
  //
  // Desktop only — see component-custom-filters.css. Phones keep the plain card.

  var MAX_CARD_PHOTOS = 8;  // plenty for a card; the product page has the rest
  var photoCache = {};      // handle -> list of photo addresses
  var photoPending = {};    // handle -> in-flight promise, so we never ask twice

  var CARD_CARET_PREV = '<svg viewBox="0 0 16 16" fill="none" aria-hidden="true" focusable="false"><path d="M10.5 1.5 4 8l6.5 6.5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  var CARD_CARET_NEXT = '<svg viewBox="0 0 16 16" fill="none" aria-hidden="true" focusable="false"><path d="m5.5 1.5 6.5 6.5-6.5 6.5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  function handleFromUrl(url) {
    var m = /\/products\/([^/?#]+)/.exec(url || '');
    return m ? m[1] : '';
  }

  // Shopify will serve any size on demand — slip _400x in before the
  // extension so a card never downloads a full-size photo.
  function sizedPhoto(url) {
    if (!url) return '';
    var full = url.indexOf('//') === 0 ? 'https:' + url : url;
    return full.replace(/\.(jpg|jpeg|png|webp|gif)(\?|$)/i, '_400x.$1$2');
  }

  function loadPhotos(handle) {
    if (!handle) return Promise.resolve([]);
    if (photoCache[handle]) return Promise.resolve(photoCache[handle]);
    if (photoPending[handle]) return photoPending[handle];
    photoPending[handle] = fetch('/products/' + encodeURIComponent(handle) + '.js')
      .then(function (res) { return res && res.ok ? res.json() : null; })
      .then(function (data) {
        var imgs = data && data.images ? data.images : [];
        var list = imgs.slice(0, MAX_CARD_PHOTOS).map(sizedPhoto).filter(Boolean);
        photoCache[handle] = list;
        delete photoPending[handle];
        return list;
      })
      .catch(function () {
        // Remember the failure too, so one bad product isn't retried forever.
        photoCache[handle] = [];
        delete photoPending[handle];
        return [];
      });
    return photoPending[handle];
  }

  // The dots are drawn from the count in the index; once the real list
  // arrives we make them match, in case a photo was added or removed since
  // the index was cached.
  function syncDots(card, total) {
    var dotsEl = card.querySelector('[data-card-dots]');
    if (!dotsEl) return;
    if (total < 2) { card.removeAttribute('data-card-photos'); dotsEl.innerHTML = ''; return; }
    if (dotsEl.children.length === total) return;
    var html = '';
    for (var i = 0; i < total; i++) {
      html += '<span class="cf-card-dot' + (i === 0 ? ' is-current' : '') + '"></span>';
    }
    dotsEl.innerHTML = html;
  }

  function showCardPhoto(card, index, list) {
    var img = card.querySelector('[data-card-image]');
    if (!img || !list.length) return;
    var i = ((index % list.length) + list.length) % list.length;
    card.setAttribute('data-photo-index', String(i));
    img.src = list[i];
    var dotsEl = card.querySelector('[data-card-dots]');
    if (!dotsEl) return;
    for (var d = 0; d < dotsEl.children.length; d++) {
      dotsEl.children[d].classList.toggle('is-current', d === i);
    }
  }

  // Put the card back to the photo it started on. Called when the pointer
  // leaves, so a grid someone has browsed through doesn't end up a patchwork
  // of whichever photo each card happened to stop on.
  function resetCardPhoto(card) {
    if ((parseInt(card.getAttribute('data-photo-index'), 10) || 0) === 0) return;
    var img = card.querySelector('[data-card-image]');
    var cached = photoCache[card.getAttribute('data-handle')];
    // The photo the card was built with; the first fetched photo is the
    // fallback in case that attribute is ever missing.
    var original = card.getAttribute('data-original-photo') || (cached && cached[0]);
    if (!img || !original) return;
    card.setAttribute('data-photo-index', '0');
    img.src = original;
    var dotsEl = card.querySelector('[data-card-dots]');
    if (!dotsEl) return;
    for (var d = 0; d < dotsEl.children.length; d++) {
      dotsEl.children[d].classList.toggle('is-current', d === 0);
    }
  }

  function stepCardPhoto(card, dir) {
    loadPhotos(card.getAttribute('data-handle')).then(function (list) {
      syncDots(card, list.length);
      if (list.length < 2) return;
      var current = parseInt(card.getAttribute('data-photo-index'), 10) || 0;
      showCardPhoto(card, current + dir, list);
    });
  }

  function closestEl(node, selector) {
    return node && node.closest ? node.closest(selector) : null;
  }

  // Hovering a card fetches its photos once, ahead of the first click, so the
  // arrows feel instant. mouseover (not mouseenter) because it bubbles, which
  // means this keeps working after the grid re-renders on every filter change.
  document.addEventListener('mouseover', function (event) {
    var card = closestEl(event.target, '[data-card-photos]');
    if (!card || card.getAttribute('data-photos-primed')) return;
    card.setAttribute('data-photos-primed', '1');
    loadPhotos(card.getAttribute('data-handle')).then(function (list) {
      syncDots(card, list.length);
    });
  });

  // Leaving the card puts its first photo back.
  document.addEventListener('mouseout', function (event) {
    var card = closestEl(event.target, '[data-card-photos]');
    if (!card) return;
    // mouseout also fires when the pointer moves between elements INSIDE the
    // card (photo -> arrow -> dots). Only act once it has really left.
    if (event.relatedTarget && card.contains(event.relatedTarget)) return;
    resetCardPhoto(card);
  });

  document.addEventListener('click', function (event) {
    var btn = closestEl(event.target, '[data-card-arrow]');
    if (!btn) return;
    // The whole card is a link to the product — stepping photos must not
    // navigate away.
    event.preventDefault();
    event.stopPropagation();
    var card = closestEl(btn, '[data-card-photos]');
    if (card) stepCardPhoto(card, btn.getAttribute('data-card-arrow') === 'next' ? 1 : -1);
  });

  // ---- Boot ---------------------------------------------------------------

  function boot() {
    document.querySelectorAll('[data-custom-filters-root]').forEach(function (root) {
      new CustomFilters(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
